// Display helpers. Money is a decimal STRING end to end — these never parse it
// into a float for display (only `numericSortKey` does, and only to ORDER rows).

export function money(value: string | number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value)
}

/** Ordering key for a decimal string column (sorting only — never display). */
export function numericSortKey(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

/** W3C timestamp (`2026-01-01T00:00:00+00:00`) -> `2026-01-01 00:00:00 UTC`. */
export function utcDateTime(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return `${d.toISOString().slice(0, 19).replace('T', ' ')} UTC`
}

/** Legacy `Exporter::getDateString`: Europe/Berlin `YmdHis` (`20260929120000`). */
export function berlinDateTime(value: string | null | undefined): string {
  if (!value) return ''
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/.exec(value)
  if (!m) return value
  return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}:${m[6]} (Berlin)`
}

/** True when `text` is a plain positive decimal (no exponent, no sign). */
export function isPositiveDecimal(text: string): boolean {
  return /^\d+(\.\d+)?$/.test(text.trim()) && Number(text) > 0
}

/** Exact `>=` on decimal strings without float rounding. */
export function decimalAtLeast(text: string, minimum: number): boolean {
  const [int = '0', frac = ''] = text.trim().split('.')
  if (!/^\d+$/.test(int) || (frac !== '' && !/^\d+$/.test(frac))) return false
  const intPart = BigInt(int)
  const min = BigInt(minimum)
  if (intPart > min) return true
  if (intPart < min) return false
  return true // int part == minimum: any fraction still satisfies >=
}

const DECIMAL = /^(\d+)(?:\.(\d+))?$/

/** Exact comparison of two plain decimal strings (-1 / 0 / 1), or null when
 * either is not a plain non-negative decimal. No float is ever involved. */
export function compareDecimal(a: string, b: string): -1 | 0 | 1 | null {
  const ma = DECIMAL.exec(a.trim())
  const mb = DECIMAL.exec(b.trim())
  if (!ma || !mb) return null
  const width = Math.max((ma[2] ?? '').length, (mb[2] ?? '').length)
  const va = BigInt(ma[1] + (ma[2] ?? '').padEnd(width, '0'))
  const vb = BigInt(mb[1] + (mb[2] ?? '').padEnd(width, '0'))
  return va < vb ? -1 : va > vb ? 1 : 0
}

/** Exact `a >= b` on decimal strings. */
export function decimalGte(a: string, b: string): boolean {
  const c = compareDecimal(a, b)
  return c !== null && c >= 0
}

/** True for a plain decimal strictly greater than zero (exact, no float). */
export function decimalIsPositive(text: string): boolean {
  return (compareDecimal(text, '0') ?? 0) > 0
}
